/**
 * Freya — the floating Amassa assistant (it replaced Roux, PHP
 * `includes/footer.php:1067-1230`, on 2026-09-23).
 *
 * Amassa is only the bridge: the turn goes to `/coportal/chatbot/message`,
 * which forwards it to Freya with a short token for the signed-in user.
 * Freya can act (file an issue, log petty cash, save a guest phone…), but
 * never silently: every write comes back as a pending card the operator
 * confirms or cancels (`FreyaPendingCard`).
 *
 * Whether it appears:
 *
 *   · `chatbot_enabled` is the ops kill switch and is honoured here.
 *   · `configured` is false when Freya is unreachable OR the caller has no
 *     Freya access, and then nothing renders.
 *   · the widget hides itself on the full-page view, so the page does not
 *     carry two copies of the same conversation.
 *   · it is COPORTAL-ONLY, as the PHP footer was.
 *   · it is DESKTOP-ONLY (owner, 2026-09-28): at/below the shell's mobile
 *     breakpoint nothing renders. Phones keep the full-page view in the rail.
 *
 * On desktop the launcher can be dragged anywhere on screen and remembers
 * the spot per browser — see `launcherPosition.ts` for the geometry.
 *
 * Replies are server HTML and go through `SafeHtml` (a whitelist rebuilt as
 * React elements), never `innerHTML`.
 */
import { COMPANY } from '@/shared/company';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { SHELL_MOBILE_QUERY, useMediaMatch } from '@/shell/useMediaMatch';
import { usePrefersReducedMotion } from '@/shared/hooks/usePrefersReducedMotion';
import {
  fetchFreyaStatus,
  sendFreyaMessage,
  type FreyaTurn,
} from './freya.api';
import { SafeHtml } from './SafeHtml';
import { FreyaPendingCard } from './FreyaPendingCard';
import {
  DEFAULT_ANCHOR,
  LAUNCHER_SIZE,
  anchorToPoint,
  clampAnchor,
  clampPoint,
  panelBox,
  pastDragThreshold,
  readLauncherAnchor,
  settleDrop,
  writeLauncherAnchor,
  type LauncherAnchor,
  type Point,
  type Viewport,
} from './launcherPosition';

export function FreyaWidget() {
  const { pathname } = useLocation();

  // Coportal only, and never on top of the full-page view.
  const inScope =
    pathname.startsWith('/coportal') && !pathname.startsWith('/coportal/chatbot');

  // Phones: no floating assistant at all. A 56px bubble pinned over a phone
  // screen covers whatever the page keeps in that corner — the Hotline's Send
  // button was the first casualty (4093c65) — and there is no room to drag it
  // anywhere better. The SAME breakpoint the shell collapses its rails at, so
  // there is one definition of "mobile" in Amassa, not two that drift.
  const isMobile = useMediaMatch(SHELL_MOBILE_QUERY);

  const status = useQuery({
    queryKey: ['freya-status'] as const,
    queryFn: ({ signal }) => fetchFreyaStatus(signal),
    staleTime: 10 * 60 * 1000,
    retry: false,
    // A phone never shows the widget, so it never asks whether to.
    enabled: inScope && !isMobile,
  });

  // A disabled or unconfigured assistant renders nothing at all, rather than
  // a button that opens onto an apology.
  const s = status.data;
  if (!inScope || isMobile || !s || !s.enabled || !s.configured) return null;

  // A separate component on purpose: the open state lives INSIDE it, so
  // crossing to a phone width unmounts panel and launcher together. An open
  // panel can never outlive the icon that closes it.
  return <FreyaFloat name={s.name} avatar={s.avatar} />;
}

// ── Launcher (draggable) ──────────────────────────────────────────────

/** One press on the launcher, from pointerdown until pointerup/cancel. */
type Press = {
  pointerId: number;
  startX: number;
  startY: number;
  /** The icon's top-left when the press began. */
  origin: Point;
  /** Crossed DRAG_THRESHOLD: this press is a drag, not a click. */
  dragging: boolean;
};

/**
 * The viewport `position: fixed` lays out in. `clientWidth/Height` exclude
 * the scrollbars, which is what `right: 24px` is measured from; `innerWidth`
 * would park a clamped icon under the vertical scrollbar. jsdom reports 0
 * for both, hence the fallback.
 */
function readViewport(): Viewport {
  const de = document.documentElement;
  return {
    w: de.clientWidth || window.innerWidth,
    h: de.clientHeight || window.innerHeight,
  };
}

function useViewport(): Viewport {
  const [vp, setVp] = useState(readViewport);
  useEffect(() => {
    const onResize = () => setVp(readViewport());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return vp;
}

/**
 * `{ right: 24, bottom: 24 }`-style offsets for an anchored box. Physical,
 * not logical, because pointer coordinates are physical; the unused pair is
 * `undefined` so React clears whatever the previous render set there.
 */
function edgeStyle(a: LauncherAnchor): React.CSSProperties {
  return {
    left: a.h === 'left' ? a.x : undefined,
    right: a.h === 'right' ? a.x : undefined,
    top: a.v === 'top' ? a.y : undefined,
    bottom: a.v === 'bottom' ? a.y : undefined,
  };
}

function FreyaFloat(props: { name: string; avatar: string }) {
  const { name, avatar } = props;
  const [open, setOpen] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const vp = useViewport();

  // `null` = the default corner. A missing, corrupt or unreadable stored
  // value lands here too (readLauncherAnchor never throws).
  const [anchor, setAnchor] = useState<LauncherAnchor | null>(readLauncherAnchor);
  // Live top-left while a drag is in flight; null otherwise.
  const [dragAt, setDragAt] = useState<Point | null>(null);
  const press = useRef<Press | null>(null);
  // Set when a drag ends, so the click the browser fires after that pointerup
  // does not also toggle the panel.
  const swallowClick = useRef(false);

  // Re-clamped against the CURRENT viewport on every render, so a resize can
  // never leave the icon (or its panel) off-screen. The clamp is not written
  // back: grow the window again and the icon returns to where it was put.
  const settled = clampAnchor(anchor ?? DEFAULT_ANCHOR, vp);
  const iconAt = dragAt ?? anchorToPoint(settled, vp);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    // Primary button only: a right-click is for the context menu.
    if (e.button !== 0) return;
    swallowClick.current = false;
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    // Start from where the icon IS, measured from its centre: the hover scale
    // grows the box around a fixed centre, and a page stylesheet may have
    // moved the icon (the Hotline lifts it above its composer). Starting from
    // our model instead would make the icon jump under the cursor.
    const origin =
      r.width > 0
        ? {
            left: r.left + r.width / 2 - LAUNCHER_SIZE / 2,
            top: r.top + r.height / 2 - LAUNCHER_SIZE / 2,
          }
        : iconAt; // no layout (jsdom)
    press.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origin,
      dragging: false,
    };
    // Keep receiving moves when the cursor outruns the icon. Optional-called
    // and guarded: synthetic pointers throw on capture in some engines.
    try {
      el.setPointerCapture?.(e.pointerId);
    } catch {
      /* moves over the icon itself still arrive */
    }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const p = press.current;
    if (!p || e.pointerId !== p.pointerId) return;
    const dx = e.clientX - p.startX;
    const dy = e.clientY - p.startY;
    if (!p.dragging) {
      if (!pastDragThreshold(dx, dy)) return;
      p.dragging = true;
    }
    setDragAt(clampPoint({ left: p.origin.left + dx, top: p.origin.top + dy }, readViewport()));
  };

  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const p = press.current;
    if (!p || e.pointerId !== p.pointerId) return;
    press.current = null;
    if (!p.dragging) return; // a click — onClick opens the chat as it always has
    swallowClick.current = true;
    const next = settleDrop(
      { left: p.origin.left + e.clientX - p.startX, top: p.origin.top + e.clientY - p.startY },
      readViewport(),
    );
    setAnchor(next);
    writeLauncherAnchor(next);
    setDragAt(null);
  };

  // The OS took the pointer (a system gesture, a native drag): put the icon
  // back where it was rather than dropping it wherever the pointer was lost.
  const onPointerCancel = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (press.current?.pointerId !== e.pointerId) return;
    press.current = null;
    setDragAt(null);
  };

  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // `detail === 0` is a keyboard (Enter/Space) or programmatic activation,
    // which can never be the tail of a drag — so a stale flag (a browser that
    // skipped the post-drag click) can never eat a keyboard open.
    if (swallowClick.current && e.detail !== 0) {
      swallowClick.current = false;
      return;
    }
    setOpen((v) => !v);
  };

  const panel = panelBox(iconAt, vp);
  const dragging = dragAt !== null;
  const label = open ? `Close ${name}` : `Ask ${name}`;

  return (
    <>
      {open && (
        <FreyaPanel
          name={name}
          avatar={avatar}
          onClose={() => setOpen(false)}
          box={{ ...edgeStyle(panel), width: panel.width, height: panel.height }}
        />
      )}
      <button
        type="button"
        className="freya-launcher"
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        // The avatar is an <img>: without this, pulling on it starts the
        // browser's native image drag (a ghost image) and cancels ours.
        onDragStart={(e) => e.preventDefault()}
        aria-expanded={open}
        aria-label={label}
        title={`${label} · drag to move`}
        style={{
          position: 'fixed',
          // In flight: plain top/left following the pointer. At rest: the
          // edges it is nearest, so a resize carries it with its corner. The
          // default corner is right/bottom 24 exactly as before, which keeps
          // page rules that nudge `inset-block-end` (Hotline) working.
          ...(dragAt ? { left: dragAt.left, top: dragAt.top } : edgeStyle(settled)),
          zIndex: 901,
          width: LAUNCHER_SIZE,
          height: LAUNCHER_SIZE,
          padding: 0,
          borderRadius: 'var(--hm-radius-pill)',
          border: '1px solid color-mix(in oklab, var(--c-ai) 45%, transparent)',
          background: 'var(--surface-2)',
          boxShadow: 'var(--q-shadow-3, 0 10px 30px rgba(0,0,0,.34))',
          cursor: dragging ? 'grabbing' : 'grab',
          // Only here, where a touch-drag on a touch-screen laptop must move
          // the icon rather than scroll the page under it.
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          display: 'grid',
          placeItems: 'center',
          overflow: 'hidden',
          // Position is never transitioned: during a drag it must track the
          // pointer 1:1, and at rest it only changes on a drop or a resize.
          transition: reducedMotion ? 'none' : 'transform .18s ease, box-shadow .18s ease',
        }}
        onMouseEnter={(e) => {
          if (!reducedMotion) e.currentTarget.style.transform = 'scale(1.06)';
        }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        {open ? <ChevronGlyph /> : <FreyaMark name={name} avatar={avatar} size={54} />}
      </button>
    </>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────

function FreyaPanel(props: {
  name: string;
  avatar: string;
  onClose: () => void;
  /** Fixed-position offsets + size, from `panelBox` next to the launcher. */
  box: React.CSSProperties;
}) {
  const titleId = useId();
  const [turns, setTurns] = useState<FreyaTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const threadId = useRef<string | null>(null);
  const logRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Pin to the newest turn, including while the reply is still pending.
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, busy]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') props.onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft('');
    setError(null);
    setTurns((t) => [...t, { role: 'user', content: text }]);
    setBusy(true);
    try {
      const reply = await sendFreyaMessage(text, threadId.current);
      threadId.current = reply.threadId;
      setTurns((t) => [...t, { role: 'assistant', content: reply.html, pending: reply.pending }]);
    } catch (err) {
      // The question stays on screen — retyping a paragraph because the
      // network blipped is the kind of small insult that stops people using
      // a tool at all.
      setError(err instanceof Error ? err.message : 'Freya could not answer.');
      setDraft(text);
      setTurns((t) => t.slice(0, -1));
    } finally {
      setBusy(false);
    }
  }, [draft, busy]);

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      className="hub-overlay"
      style={{
        position: 'fixed',
        // Beside the launcher, wherever it has been dragged. For the default
        // corner this is the old right 24 / bottom 92, 380 × min(520, vh−150).
        ...props.box,
        zIndex: 901,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // Opaque overlay, never raw --bg-card: this floats over page content
        // and the glass version was the readability bug CLAUDE.md records.
        background: 'var(--surface-2)',
        border: '1px solid var(--border-strong)',
        borderRadius: 'var(--hm-radius-lg)',
        boxShadow: 'var(--q-shadow-3, 0 18px 54px rgba(0,0,0,.42))',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px',
          borderBottom: '1px solid var(--border)',
          background:
            'linear-gradient(180deg, color-mix(in oklab, var(--c-ai) 15%, transparent), transparent)',
          flexShrink: 0,
        }}
      >
        <FreyaMark name={props.name} avatar={props.avatar} size={30} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            id={titleId}
            style={{
              margin: 0,
              font: '600 13px/1.2 var(--hm-display)',
              color: 'var(--text)',
              letterSpacing: '-0.01em',
            }}
          >
            {props.name}
          </p>
          <p
            style={{
              margin: 0,
              font: '500 10px/1.4 var(--hm-mono)',
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: 'var(--text-dim)',
            }}
          >
            {COMPANY.name} assistant
          </p>
        </div>
        <button
          type="button"
          onClick={props.onClose}
          aria-label="Close"
          style={iconButton}
        >
          <ChevronGlyph />
        </button>
      </header>

      <div
        ref={logRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <Bubble role="assistant" avatar={props.avatar} name={props.name}>
          Hi, I&rsquo;m {props.name}. Ask me about a booking, a log line or how
          something in Amassa works.
          <span
            style={{
              display: 'block',
              marginBlockStart: 6,
              font: '500 10px/1.5 var(--hm-mono)',
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: 'var(--text-dim)',
            }}
          >
            Anything I save waits for your Confirm
          </span>
        </Bubble>

        {turns.map((t, i) => (
          <div key={i} style={{ display: 'contents' }}>
            <Bubble role={t.role} avatar={props.avatar} name={props.name}>
              {t.role === 'assistant' ? <SafeHtml html={t.content} /> : t.content}
            </Bubble>
            {(t.pending ?? []).map((p) => (
              <div key={p.id} style={{ marginInlineStart: 32 }}>
                <FreyaPendingCard item={p} threadId={threadId.current} />
              </div>
            ))}
          </div>
        ))}

        {busy && (
          <Bubble role="assistant" avatar={props.avatar} name={props.name}>
            <span style={{ color: 'var(--text-dim)' }}>thinking&hellip;</span>
          </Bubble>
        )}

        {error && (
          <div
            role="alert"
            style={{
              font: '500 12px/1.5 var(--hm-mono)',
              color: 'var(--heat)',
              background: 'color-mix(in oklab, var(--heat) 12%, transparent)',
              border: '1px solid color-mix(in oklab, var(--heat) 32%, transparent)',
              borderRadius: 'var(--hm-radius-md)',
              padding: '8px 10px',
            }}
          >
            {error}
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 6,
          alignItems: 'flex-end',
          padding: 10,
          borderTop: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <label htmlFor={`${titleId}-input`} className="sr-only" style={srOnly}>
          Message {props.name}
        </label>
        <textarea
          id={`${titleId}-input`}
          ref={inputRef}
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter breaks the line — the convention every
            // chat surface these operators use already follows.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="Ask about a booking or a workflow…"
          style={{
            flex: 1,
            resize: 'none',
            maxHeight: 96,
            padding: '9px 11px',
            font: '400 13px/1.45 var(--hm-display)',
            color: 'var(--text)',
            background: 'var(--bg-elev)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--hm-radius-sm)',
            outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={busy || draft.trim().length === 0}
          aria-label="Send"
          style={{
            ...iconButton,
            width: 36,
            height: 36,
            background: 'color-mix(in oklab, var(--c-ai) 22%, transparent)',
            border: '1px solid color-mix(in oklab, var(--c-ai) 45%, transparent)',
            borderRadius: 'var(--hm-radius-sm)',
            opacity: busy || draft.trim().length === 0 ? 0.45 : 1,
            cursor: busy || draft.trim().length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          <SendGlyph />
        </button>
      </div>
    </div>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────

function Bubble(props: {
  role: 'user' | 'assistant';
  avatar: string;
  name: string;
  children: React.ReactNode;
}) {
  const mine = props.role === 'user';
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
        maxWidth: '90%',
        alignSelf: mine ? 'flex-end' : 'flex-start',
        flexDirection: mine ? 'row-reverse' : 'row',
      }}
    >
      {!mine && <FreyaMark name={props.name} avatar={props.avatar} size={24} />}
      <div
        style={{
          font: '400 13px/1.5 var(--hm-display)',
          color: 'var(--text)',
          background: mine
            ? 'color-mix(in oklab, var(--c-ai) 18%, transparent)'
            : 'var(--bg-elev)',
          border: '1px solid var(--border)',
          borderRadius: mine ? '12px 12px 2px 12px' : '2px 12px 12px 12px',
          padding: '8px 11px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {props.children}
      </div>
    </div>
  );
}

/**
 * The avatar, with the same two-branch fallback PHP has: the configured
 * image, else a glyph. A broken image URL falls back too — the production
 * avatar is hosted on the legacy PHP docroot, which is scheduled for
 * replacement, and an assistant whose face 404s should degrade rather than
 * render a torn-image icon.
 */
function FreyaMark(props: { name: string; avatar: string; size: number }) {
  const [broken, setBroken] = useState(false);
  const box: React.CSSProperties = {
    width: props.size,
    height: props.size,
    borderRadius: 'var(--hm-radius-pill)',
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
  };

  if (props.avatar && !broken) {
    return (
      <img
        src={props.avatar}
        alt=""
        onError={() => setBroken(true)}
        style={{ ...box, objectFit: 'cover' }}
      />
    );
  }
  return (
    <span
      style={{
        ...box,
        background: 'color-mix(in oklab, var(--c-ai) 26%, transparent)',
        border: '1px solid color-mix(in oklab, var(--c-ai) 45%, transparent)',
      }}
    >
      <BotGlyph size={Math.round(props.size * 0.5)} />
    </span>
  );
}

function BotGlyph({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="var(--c-ai)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 8V4H8" /><rect width="16" height="12" x="4" y="8" rx="2" />
      <path d="M2 14h2" /><path d="M20 14h2" /><path d="M15 13v2" /><path d="M9 13v2" />
    </svg>
  );
}

function ChevronGlyph() {
  return (
    <svg
      width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function SendGlyph() {
  return (
    <svg
      width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="var(--c-ai)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" />
    </svg>
  );
}

const iconButton: React.CSSProperties = {
  width: 28,
  height: 28,
  display: 'grid',
  placeItems: 'center',
  background: 'transparent',
  border: '1px solid transparent',
  borderRadius: 'var(--hm-radius-sm)',
  cursor: 'pointer',
  flexShrink: 0,
  padding: 0,
};

const srOnly: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};
